import introJs from 'intro.js'
import 'intro.js/introjs.css'

class TourService {
  constructor() {
    this.tours = {
      essentials: this.getEssentialsTour(),
      advanced: this.getAdvancedTour(),
      admin: this.getAdminTour()
    }
    this.router = null
    this.componentInstance = null
    this.currentIntro = null
  }

  setContext(router, component) {
    this.router = router
    this.componentInstance = component
  }

  getEssentialsTour() {
    return [
      {
        intro: 'Welcome to <strong>DGTasks!</strong> This is your central hub for managing all work items. Let\'s explore the key features that will keep you productive.',
        position: 'center'
      },
      {
        element: '[data-tour="view-mode-toggle"]',
        intro: 'Switch between <strong>Tasks view</strong> for list management and <strong>Schedule view</strong> for calendar-based planning with drag and drop scheduling.',
        position: 'bottom'
      },
      {
        element: '[data-tour="personal-tasks-tab"]',
        intro: '<strong>Personal Task List</strong> - tasks you\'ve created for yourself, or others have assigned to you. This is where you manage your own work items and track progress.',
        position: 'bottom'
      },
      {
        element: '[data-tour="assigned-tasks-tab"]', 
        intro: '<strong>Tasks Assigned To Others</strong> - work you\'ve delegated to team members. Use this to track team assignments and ensure accountability.',
        position: 'bottom'
      },
      {
        element: '[data-tour="create-task-btn"]',
        intro: '<strong>Create Personal Tasks</strong> - Click here to add new work items to your own task list. Perfect for planning your day.',
        position: 'left'
      },
      {
        element: '[data-tour="assign-task-btn"]',
        intro: '<strong>Assign Tasks to Others</strong> - Delegate work to team members and track their progress. Essential for team management.',
        position: 'left'
      },
      {
        element: '[data-tour="quick-task-input"]',
        intro: '<strong>Quick Task Creation!</strong> Type a task name and press Enter for instant creation. Perfect for rapid brainstorming and capturing ideas.',
        position: 'top'
      },
      {
        element: '[data-tour="quick-task-company"]',
        intro: 'Optionally assign quick tasks to specific <strong>companies</strong>. Leave empty for personal tasks.',
        position: 'top'
      },
      {
        element: '[data-tour="first-task-name"]',
        intro: 'Click any <strong>task name</strong> to open the detailed Task Navigation Panel. This is your central place for comprehensive task management.',
        position: 'right'
      },
      {
        element: '[data-tour="task-details-drawer"]',
        intro: '🎯 <strong>Task Navigation Panel</strong> - Your complete task management hub! Here you can edit all task details, manage assignments, add descriptions, create subtasks, attach files, leave comments with replies, and track activity history.',
        position: 'left'
      },
      {
        element: '[data-tour="task-title-field"]',
        intro: '<strong>Task Title</strong> - Click to edit the task name directly. Changes save automatically when you click outside.',
        position: 'left'
      },
      {
        element: '[data-tour="property-grid-compact"]',
        intro: '<strong>Task Properties</strong> - Quickly update priority, status, due date, and assignees. All changes are instant with visual feedback.',
        position: 'left'
      },
      {
        element: '[data-tour="description-section"]',
        intro: '<strong>Rich Text Descriptions</strong> - Add detailed context with formatting, links, and structured content using the built-in editor.',
        position: 'left'
      },
      {
        element: '[data-tour="subtasks-section"]',
        intro: '<strong>Subtasks</strong> - Break down complex work into manageable pieces.',
        position: 'left'
      },
      {
        element: '[data-tour="attachments-section"]',
        intro: '<strong>File Attachments</strong> - Upload documents, images, and resources directly to tasks. Perfect for specifications, mockups, or reference materials.',
        position: 'left'
      },
      {
        element: '[data-tour="activity-section"]',
        intro: '<strong>Comments & Activity</strong> - Leave comments, reply to discussions, and track all task changes with a complete audit trail.',
        position: 'left'
      },
      {
        element: '[data-tour="comment-footer"]',
        intro: '<strong>Add Comments</strong> - Start discussions, ask questions, or provide updates..',
        position: 'left'
      },
      {
        element: '[data-tour="task-status"]',
        intro: 'Click <strong>status chips</strong> to quickly update task progress.',
        position: 'top'
      },
      {
        element: '[data-tour="task-priority"]', 
        intro: 'Click <strong>priority indicators</strong> to adjust task importance. Visual cues help you focus on what matters most.',
        position: 'top'
      },
      {
        element: '[data-tour="schedule-mode-btn"]',
        intro: 'Switch to <strong>Schedule view</strong> to see tasks on a calendar. Drag unscheduled tasks to time slots to plan your day!',
        position: 'bottom'
      },
      {
        element: '[data-tour="notifications-badge"]',
        intro: 'Your <strong>notification center</strong> - stay updated on task assignments, status changes, and team activity in real-time.',
        position: 'bottom'
      },
      {
        intro: '🎉 <strong>Congratulations!</strong> You\'ve completed the essentials tour. You\'re now ready to be productive with DGTasks. Start by creating your first task!',
        position: 'center'
      }
    ]
  }

  getAdvancedTour() {
    return [
      {
        element: '[data-tour="bulk-select-checkbox"]',
        intro: 'Select multiple tasks using checkboxes to perform <strong>bulk operations</strong> like status updates, priority changes, or deletions.',
        position: 'right'
      },
      {
        element: '[data-tour="task-filters"]',
        intro: 'Filter tasks by <strong>status, priority, company, or assignee</strong> to focus on specific work and reduce visual clutter.',
        position: 'bottom'
      },
      {
        element: '[data-tour="task-actions-menu"]',
        intro: 'Each task has an <strong>actions menu</strong> with options to edit, delete, duplicate, or perform quick status changes.',
        position: 'left'
      },
      {
        element: '[data-tour="user-selector"]',
        intro: '<strong>Admins and managers</strong> can view tasks for specific users to help with team management and workload balancing.',
        position: 'bottom'
      },
      {
        element: '[data-tour="bulk-actions-bar"]',
        intro: 'When tasks are selected, the <strong>bulk actions bar</strong> appears with options to update multiple tasks at once.',
        position: 'top'
      }
    ]
  }

  getAdminTour() {
    return [
      {
        element: '[data-tour="overview-nav"]',
        intro: "<strong>Manager's Hub</strong> provides company-wide visibility into all team tasks, bottlenecks, and productivity metrics — plus tools to give direct feedback to your team members.",
        position: 'bottom'
      },
      {
        element: '[data-tour="users-nav"]',
        intro: '<strong>User Management</strong> for adding team members, configuring permissions, and managing roles.',
        position: 'bottom'
      },
      {
        element: '[data-tour="companies-nav"]',
        intro: '<strong>Company/Project Management</strong> for organizing work across different clients and initiatives.',
        position: 'bottom'
      },
      {
        element: '[data-tour="goals-nav"]',
        intro: '<strong>Goals Management</strong> for setting and tracking team objectives and key results.',
        position: 'bottom'
      }
    ]
  }

  // Helper method to refresh tour highlighting
  refreshTourHighlight() {
    if (this.currentIntro) {
      try {
        this.currentIntro.refresh()
        console.log('Tour: Refreshed highlight positioning')
      } catch (error) {
        console.warn('Tour: Failed to refresh highlight:', error)
      }
    }
  }

  // Helper methods for interactive tour actions
  triggerTabClick(tabIndex) {
    setTimeout(() => {
      try {
        console.log(`Tour: Attempting to switch to tab ${tabIndex}`)
        console.log('Tour: Component instance:', this.componentInstance)
        
        if (this.componentInstance && this.componentInstance.activeTab !== undefined) {
          console.log(`Tour: Setting activeTab from ${this.componentInstance.activeTab} to ${tabIndex}`)
          this.componentInstance.activeTab = tabIndex
          console.log(`Tour: activeTab is now: ${this.componentInstance.activeTab}`)
          // Refresh tour highlighting after tab change
          setTimeout(() => this.refreshTourHighlight(), 300)
        } else {
          console.log('Tour: Component instance not available, trying DOM approach')
          const tabs = document.querySelectorAll('.v-tabs .v-tab')
          console.log('Tour: Found tabs:', tabs.length)
          if (tabs[tabIndex]) {
            console.log(`Tour: Clicking tab at index ${tabIndex}`)
            tabs[tabIndex].click()
            // Refresh tour highlighting after tab change
            setTimeout(() => this.refreshTourHighlight(), 300)
          } else {
            console.warn(`Tour: Tab ${tabIndex} not found, available tabs: ${tabs.length}`)
          }
        }
      } catch (error) {
        console.warn('Tour: Failed to trigger tab click:', error)
      }
    }, 200)
  }

  switchToScheduleView() {
    setTimeout(() => {
      try {
        if (this.componentInstance && this.componentInstance.viewMode !== undefined) {
          this.componentInstance.viewMode = 'schedule'
          console.log('Tour: Switched to schedule view')
        } else {
          const scheduleBtn = document.querySelector('[data-tour="schedule-mode-btn"]')
          if (scheduleBtn) {
            scheduleBtn.click()
            console.log('Tour: Clicked schedule button')
          } else {
            console.warn('Tour: Schedule button not found')
          }
        }
      } catch (error) {
        console.warn('Tour: Failed to switch to schedule view:', error)
      }
    }, 200)
  }

  focusQuickTaskInput() {
    setTimeout(() => {
      try {
        const input = document.querySelector('[data-tour="quick-task-input"] input')
        if (input) {
          input.focus()
          input.placeholder = 'Try typing a task name here!'
          console.log('Tour: Focused quick task input')
        } else {
          console.warn('Tour: Quick task input not found')
        }
      } catch (error) {
        console.warn('Tour: Failed to focus quick task input:', error)
      }
    }, 200)
  }

  highlightFirstTask() {
    setTimeout(() => {
      try {
        const firstTask = document.querySelector('[data-tour="first-task-name"]')
        if (firstTask) {
          firstTask.style.background = 'rgba(25, 118, 210, 0.1)'
          firstTask.style.transition = 'background 0.3s ease'
          console.log('Tour: Highlighted first task')
          
          // Reset after a moment
          setTimeout(() => {
            firstTask.style.background = ''
          }, 2000)
        } else {
          console.warn('Tour: First task element not found for highlighting')
        }
      } catch (error) {
        console.warn('Tour: Failed to highlight first task:', error)
      }
    }, 200)
  }

  openTaskNavigationForTour() {
    setTimeout(() => {
      // First highlight the task name
      this.highlightFirstTask()
      
      // Then click on the first task to open the navigation panel
      const firstTaskName = document.querySelector('[data-tour="first-task-name"]')
      if (firstTaskName) {
        firstTaskName.click()
        
        // Wait for the navigation panel to open before continuing
        this.waitForTaskNavigationToOpen(() => {
          // Panel is ready, tour can continue
          console.log('Task navigation panel opened for tour')
          
          // Prevent the panel from closing during tour by temporarily disabling click-outside
          this.preventTaskNavigationClose()
        })
      } else {
        console.warn('First task element not found for tour')
      }
    }, 200)
  }

  preventTaskNavigationClose() {
    // Add a class to the body to indicate we're in tour mode
    document.body.classList.add('tour-mode-active')
    
    // Find the task navigation drawer and prevent outside clicks
    const drawer = document.querySelector('.task-details-drawer')
    if (drawer) {
      // Store original click handler
      this.originalDrawerClickHandler = drawer.onclick
      
      // Temporarily disable the v-click-outside directive by adding a tour class
      drawer.classList.add('tour-active-no-close')
      console.log('Tour: Prevented task navigation from closing')
    }
  }

  allowTaskNavigationClose() {
    // Remove tour mode class
    document.body.classList.remove('tour-mode-active')
    
    // Re-enable normal closing behavior
    const drawer = document.querySelector('.task-details-drawer')
    if (drawer) {
      drawer.classList.remove('tour-active-no-close')
      console.log('Tour: Re-enabled task navigation closing')
    }
  }

  waitForTaskNavigationToOpen(callback) {
    // Wait up to 3 seconds for the task navigation panel to appear
    let attempts = 0
    const maxAttempts = 30 // 3 seconds with 100ms intervals
    
    const checkForPanel = () => {
      const panel = document.querySelector('[data-tour="task-details-drawer"]')
      if (panel && panel.offsetWidth > 0) {
        // Panel is open and visible
        if (callback) callback()
        return true
      }
      
      attempts++
      if (attempts < maxAttempts) {
        setTimeout(checkForPanel, 100)
      } else {
        console.warn('Task navigation panel did not open in time for tour')
        if (callback) callback() // Continue anyway
      }
      return false
    }
    
    checkForPanel()
  }

  closeTaskNavigationAfterTour() {
    setTimeout(() => {
      try {
        // First re-enable normal closing behavior
        this.allowTaskNavigationClose()
        
        // Then close the task navigation panel
        const closeBtn = document.querySelector('.task-details-drawer .close-btn')
        if (closeBtn) {
          closeBtn.click()
          console.log('Tour: Closed task navigation panel')
        } else {
          // Fallback: try clicking outside the panel
          const overlay = document.querySelector('.v-overlay')
          if (overlay) {
            overlay.click()
            console.log('Tour: Closed task navigation via overlay')
          } else {
            console.warn('Tour: Could not find close button or overlay')
          }
        }
      } catch (error) {
        console.warn('Tour: Failed to close task navigation panel:', error)
      }
    }, 200)
  }

  triggerCreateTaskDialog() {
    setTimeout(() => {
      if (this.componentInstance && this.componentInstance.openNewTaskDialog) {
        this.componentInstance.openNewTaskDialog('self')
      }
    }, 200)
  }

  closeTaskDialog() {
    setTimeout(() => {
      if (this.componentInstance && this.componentInstance.closeTaskDialog) {
        this.componentInstance.closeTaskDialog()
      }
    }, 200)
  }

  // Navigation helpers
  async navigateToPage(routeName, params = {}) {
    if (this.router) {
      try {
        await this.router.push({ name: routeName, params })
        // Wait for navigation to complete
        await new Promise(resolve => setTimeout(resolve, 800))
      } catch (error) {
        console.warn('Navigation failed during tour:', error)
      }
    }
  }

  // Main tour control methods
  startTour(tourName = 'essentials') {
    const tour = this.tours[tourName]
    if (!tour) {
      console.warn(`Tour "${tourName}" not found`)
      return
    }

    // Ensure we're on the dashboard for essentials tour
    if (tourName === 'essentials') {
      if (this.router && this.router.currentRoute.name !== 'Dashboard') {
        this.router.push({ name: 'Dashboard' })
        setTimeout(() => this.initializeTour(tour, tourName), 1000)
        return
      }
    }

    this.initializeTour(tour, tourName)
  }

  initializeTour(tour, tourName) {
    // Clean up any existing tour
    if (this.currentIntro) {
      this.currentIntro.exit()
    }

    this.currentIntro = introJs().setOptions({
      steps: tour,
      showProgress: true,
      showBullets: false,
      exitOnOverlayClick: false,
      exitOnEsc: true,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Got it!',
      skipLabel: 'Skip tour',
      disableInteraction: true, // Disable interaction with page elements during tour
      scrollToElement: true,
      scrollPadding: 50, // Increased padding for better visibility
      tooltipClass: 'dgtasks-tour-tooltip',
      highlightClass: 'dgtasks-tour-highlight',
      helperElementPadding: 15, // Increased padding around highlighted elements
      showStepNumbers: false,
      autoPosition: true, // Let intro.js automatically position tooltips
      positionPrecedence: ["bottom", "top", "right", "left"] // Preferred positioning order
    })

    // Hook into step changes for interactive features
    let currentStepIndex = 0 // Track step manually since intro.js internal properties are unreliable
    
    this.currentIntro.onchange((targetElement) => {
      currentStepIndex++
      console.log(`Tour: onchange triggered, step index: ${currentStepIndex}, targetElement:`, targetElement)
      
      // Add step-specific classes for custom styling
      document.body.className = document.body.className.replace(/tour-step-\d+/g, '')
      document.body.classList.add(`tour-step-${currentStepIndex}`)
      
      // Handle specific step actions based on current step index
      // Use setTimeout to ensure DOM is ready after step change
      setTimeout(() => {
        switch (currentStepIndex) {
          case 3: // Personal tasks tab step 
            console.log('Tour: Triggering tab click for personal tasks (step 3)')
            this.triggerTabClick(0)
            break
          case 4: // Assigned tasks tab step 
            console.log('Tour: Triggering tab click for assigned tasks (step 4)') 
            this.triggerTabClick(1)
            break
            case 5:
            this.triggerTabClick(0) // Go back to tab 0 first
            break
          case 7: // Quick task input step
            console.log('Tour: Focusing quick task input (step 7)')
            this.focusQuickTaskInput()
            break
          case 9: // First task name step - open task navigation
            console.log('Tour: Opening task navigation (step 9)')
            this.openTaskNavigationForTour()
            break
          case 17: // Comment footer step - close task navigation
            console.log('Tour: Closing task navigation (step 17)')
            this.closeTaskNavigationAfterTour()
            break
          case 20: // Schedule mode button step
            console.log('Tour: Switching to schedule view (step 20)')
            this.switchToScheduleView()
            break
          default:
            console.log(`Tour: No action for step ${currentStepIndex}`)
        }
      }, 300)
    })

    this.currentIntro.oncomplete(() => {
      this.markTourCompleted(tourName)
      this.showCompletionMessage(tourName)
      this.cleanupTourState()
    })

    this.currentIntro.onexit(() => {
      // Covers Skip tour, Esc, and any other early exit - once a user has been
      // shown a tour and dismissed it, it shouldn't auto-start again on its own.
      // Manually starting a tour from the Guided Tours menu bypasses this.
      this.markTourDismissed(tourName)
      this.cleanupTourState()
    })

    // Start the tour
    this.currentIntro.start()
    return this.currentIntro
  }

  showCompletionMessage(tourName) {
    if (this.componentInstance && this.componentInstance.$toast) {
      const messages = {
        essentials: '🎉 Essentials tour completed! You\'re ready to be productive with DGTasks.',
        advanced: '🚀 Advanced features tour completed! You\'re now a power user.',
        admin: '👑 Admin tour completed! You have full mastery of the system.'
      }
      this.componentInstance.$toast.success(messages[tourName] || 'Tour completed!')
    }
  }

  cleanupTourState() {
    // Reset component states to defaults
    if (this.componentInstance) {
      if (this.componentInstance.viewMode !== undefined) {
        this.componentInstance.viewMode = 'tasks'
      }
      if (this.componentInstance.activeTab !== undefined) {
        this.componentInstance.activeTab = 0
      }
    }

    // Re-enable task navigation closing
    this.allowTaskNavigationClose()

    // Remove step-specific classes
    document.body.className = document.body.className.replace(/tour-step-\d+/g, '')
    
    // Clear current intro reference
    this.currentIntro = null
  }

  // Tour state management
  hasCompletedTour(tourName) {
    return localStorage.getItem(`tour_completed_${tourName}`) === 'true'
  }

  markTourCompleted(tourName) {
    localStorage.setItem(`tour_completed_${tourName}`, 'true')
    localStorage.setItem(`tour_completed_${tourName}_date`, new Date().toISOString())
  }

  // "Dismissed" covers Skip tour / Esc / any exit before completion. Distinct
  // from "completed" so the Guided Tours menu can still show accurate
  // completion checkmarks while auto-start treats dismissal the same as done.
  hasDismissedTour(tourName) {
    return localStorage.getItem(`tour_dismissed_${tourName}`) === 'true'
  }

  markTourDismissed(tourName) {
    localStorage.setItem(`tour_dismissed_${tourName}`, 'true')
  }

  // Whether a tour has already been shown to this user, whether they finished
  // it or dismissed it early. Auto-start should only fire when this is false.
  hasSeenTour(tourName) {
    return this.hasCompletedTour(tourName) || this.hasDismissedTour(tourName)
  }

  resetTours() {
    const tourNames = Object.keys(this.tours)
    tourNames.forEach(tourName => {
      localStorage.removeItem(`tour_completed_${tourName}`)
      localStorage.removeItem(`tour_completed_${tourName}_date`)
      localStorage.removeItem(`tour_dismissed_${tourName}`)
    })
  }

  // Get tour completion stats
  getTourStats() {
    const tourNames = Object.keys(this.tours)
    const stats = {
      total: tourNames.length,
      completed: 0,
      completionDates: {}
    }

    tourNames.forEach(tourName => {
      if (this.hasCompletedTour(tourName)) {
        stats.completed++
        const date = localStorage.getItem(`tour_completed_${tourName}_date`)
        if (date) {
          stats.completionDates[tourName] = date
        }
      }
    })

    return stats
  }

  // Force exit current tour
  exitCurrentTour() {
    if (this.currentIntro) {
      this.currentIntro.exit()
    }
  }
}

export default new TourService()